import type { ResourceSection as Resource } from "../../types/resourceSection";
import { awsConfig } from "../config/aws-config";
import { toast } from "react-toastify";

/**
 * Service object to interact with the Resource API endpoints.
 */
export const resourceService = {
  /**
   * Fetches all resources.
   * Endpoint: GET /resources
   * @returns Array of resources.
   */
  getResources: async (): Promise<Resource[]> => {
    const url = `${awsConfig.apiEndpoint}/resources`;
    
    try {
      
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
      });
      
      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to fetch resources");
      }
      
      const json = await response.json();
      
      // Extract resources array from the API response.
      return json.success ? json.resources : [];
    } catch (error: any) {
      toast.error(error.message || "Failed to fetch resources");
      throw error;
    }
  },

  /**
   * Creates a new resource.
   * Endpoint: POST /resources
   * @param data - Partial resource data.
   * @param token - Authorization token.
   * @returns The created resource.
   */
  createResource: async (data: Partial<Resource>, token: string): Promise<Resource> => {
    const url = `${awsConfig.apiEndpoint}/resources`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        mode: "cors",
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to create resource");
      }
      const result = await response.json();
      // Return the inner resource object
      return result.resource;
    } catch (error: any) {
      toast.error(error.message || "Failed to create resource");
      throw error;
    }
  },
  
  /**
   * Updates an existing resource.
   * Endpoint: PUT /resources/{id}
   * @param id - ID of the resource to update.
   * @param data - Partial resource data for update.
   * @param token - Authorization token.
   * @returns The updated resource.
   */
  updateResource: async (id: string, data: Partial<Resource>, token: string): Promise<Resource> => {
    const url = `${awsConfig.apiEndpoint}/resources/${id}`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        mode: "cors",
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to update resource");
      }
      const result = await response.json();
      // Return the inner resource object
      return result.resource;
    } catch (error: any) {
      toast.error(error.message || "Failed to update resource");
      throw error;
    }
  },

  /**
   * Deletes a resource.
   * Endpoint: DELETE /resources/{id}
   * @param id - ID of the resource to delete.
   * @param token - Authorization token.
   * @returns Void promise.
   */
  deleteResource: async (id: string, token: string): Promise<void> => {
    const url = `${awsConfig.apiEndpoint}/resources/${id}`;
    
    try {
      
      const response = await fetch(url, {
        method: "DELETE",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        mode: "cors",
      });
      
      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to delete resource");
      }
      
      
      // Optionally, you could return some confirmation data here
    } catch (error: any) {
      toast.error(error.message || "Failed to delete resource");
      throw error;
    }
  },
};

/**
 * Helper function to parse error responses from the API.
 * @param response - Fetch Response object.
 * @returns Object with a message property.
 */
const parseErrorResponse = async (response: Response): Promise<{ message: string }> => {
  try {
    const data = await response.json();
    return { message: data.message || "An error occurred" };
  } catch {
    return { message: response.statusText || "An error occurred" };
  }
};
